import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css"; // Optional theme
import { globalErrorHandler } from "../../../utils/GlobalErrorHandler";

// utils/dateUtils.js
const formatDate = (utcString) => {
  if (!utcString) return "";
  const date = new Date(utcString);
  return date.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const GuestLocation = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [guestInfo, setGuestInfo] = useState(null);
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // Fetch guest's latest location & info
  const fetchGuestInfo = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-latest-location/${id}`,
        { withCredentials: true }
      );
      if (res.data.status) {
        setGuestInfo(res.data.location || {});
      } else {
        Swal.fire("Info", "No location data found for this guest.", "info");
      }
    } catch (error) {
      globalErrorHandler(error, navigate);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all recorded locations
  const fetchLocations = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_WEB_SERVICES_URL}/admin/get-locations/${id}`,
        { withCredentials: true }
      );
      if (res.data.status) {
        setLocations(res.data.locations || []);
        setFilteredLocations(res.data.locations || []);
      } else {
        setLocations([]);
        setFilteredLocations([]);
      }
    } catch (error) {
      globalErrorHandler(error, navigate);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchGuestInfo();
    fetchLocations();
  }, [id]);

  // Filter locations by date range
  useEffect(() => {
    if (!fromDate && !toDate) {
      setFilteredLocations(locations);
      return;
    }

    const filtered = locations.filter((loc) => {
      const locDate = new Date(loc.created_at);
      if (fromDate && toDate) return locDate >= fromDate && locDate <= toDate;
      if (fromDate) return locDate >= fromDate;
      if (toDate) return locDate <= toDate;
      return true;
    });

    setFilteredLocations(filtered);
  }, [fromDate, toDate, locations]);

  // Open Google Maps directly
  const openInGoogleMaps = (lat, lng) => {
    if (!lat || !lng) {
      Swal.fire("Error", "Location coordinates are missing.", "error");
      return;
    }
    window.open(`https://www.google.com/maps?q=${lat},${lng}&z=15`, "_blank");
  };

  return (
    <div className="container-fluid px-4 mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Location History</h3>
      </div>

      {isLoading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" variant="dark" />
        </div>
      ) : guestInfo ? (
        <>
          {/* Guest Info Card */}
          <div className="card shadow-sm p-4 mb-4">
            <h5 className="mb-2">
              Live Location of{" "}
              <strong>
                {guestInfo.name} ({`BBM${String(guestInfo.guest_id).padStart(4, "0")}`})
              </strong>
            </h5>
            <p className="mb-0">
              Current Status:{" "}
              <span className={guestInfo.is_enabled ? "text-success fw-bold" : "text-danger fw-bold"}>
                {guestInfo.tracking_enabled ? "Location Tracking Enabled" : "Location Tracking Disabled"}
              </span>
            </p>

            {guestInfo.latitude && guestInfo.longitude && (
              <p className="mt-2">
                Last Known Location:{" "}
                <Button
                  size="sm"
                  variant="info"
                  className="mx-2"
                  onClick={() => openInGoogleMaps(guestInfo.latitude, guestInfo.longitude)}
                >
                  View on Map
                </Button>
                on <small className="text-muted"> {guestInfo.created_at
                            ? new Date(guestInfo.created_at + "Z").toLocaleString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })
                            : "-"}</small>
              </p>
            )}
          </div>

          {/* Filter Controls with Flatpickr */}
          <div className="mb-3 d-flex gap-2">
            <Flatpickr
              options={{ enableTime: true, dateFormat: "Y-m-d H:i" }}
              placeholder="From Date"
              value={fromDate}
              onChange={(date) => setFromDate(date[0] || null)}
              className="form-control"
            />
            <Flatpickr
              options={{ enableTime: true, dateFormat: "Y-m-d H:i" }}
              placeholder="To Date"
              value={toDate}
              onChange={(date) => setToDate(date[0] || null)}
              className="form-control"
            />
            <Button
              variant="secondary"
              onClick={() => {
                setFromDate(null);
                setToDate(null);
              }}
            >
              Reset
            </Button>
          </div>

          {/* Locations Table */}
          <div className="card shadow mt-4">
            <div className="card-body p-4">
              <table className="table table-bordered table-hover shadow-sm nowrap">
                <thead className="table-success">
                  <tr>
                    <th style={{ width: "60%" }}>Date & Time</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLocations.length === 0 ? (
                    <tr>
                      <td colSpan="2" className="text-center">
                        No location records found.
                      </td>
                    </tr>
                  ) : (
                    filteredLocations.map((loc) => (
                      <tr key={loc.id}>
                        <td>
                           {loc.created_at
                            ? new Date(loc.created_at + "Z").toLocaleString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })
                            : "-"}
                          
                          </td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            onClick={() => openInGoogleMaps(loc.latitude, loc.longitude)}
                          >
                            View on Map
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <p>No data available.</p>
      )}
    </div>
  );
};

export default GuestLocation;
